import React, {ChangeEvent, memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody, Checkbox} from "@material-tailwind/react";
import {API} from "../../../types/API";
import {AxiosResponse} from "axios";
import {PermissionGroup, PermissionsResponse, Role} from "../../../types/Responses/RolesAndPermissions";
import {Input} from "@material-tailwind/react/components/Input";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../store";
import {getError, hasError} from "../../../hooks/errors";
import {clear, validateData} from "../../../reducers/validationReducer";
import {InputType} from "../../../types/InputType";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useNavigate, useParams} from "react-router-dom";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import FormSkeleton from "../../../components/Skeletons/FormSkeleton";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useErrorHandler} from "react-error-boundary";
import Forbidden from "../../../components/Forbidden";
import {useUserContext} from "../../../types/contexts/UserContext";
import {useTranslation} from "react-i18next";
import Card from "../../../components/Card";
import {setTitle} from "../../../hooks/setTitle";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import {removeDataTableRequest} from "../../../reducers/dataTableSearch";
import {CountriesShortResponse} from "../../../types/Responses/CountriesResponse";
import {AllRegionsResponse} from "../../../types/Responses/RegionsResponse";
import DropdownInput from "../../../components/DropdownInput";
import CountryAvatar from "../../../components/CountryAvatar";
import {setSidebarTitle} from "../../../reducers/sidebar";

const RoleForm = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    const toast = useToastContext();
    const navigate = useNavigate();
    const handleError = useErrorHandler();
    const user = useUserContext();

    const [listCountries, setListCountries] = useState<CountriesShortResponse>();
    const [allRegions, setAllRegions] = useState<AllRegionsResponse>();

    const [permissions, setPermissions] = useState<PermissionGroup[]>();
    const [name, setName] = useState<string>();
    const [regions, setRegions] = useState<number[]>([]);
    const [countries, setCountries] = useState<number[]>([]);
    const [selected, setSelected] = useState<number[]>([]);
    const [isInProgress, setInProgress] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean>(false);
    const [formIsReady, setFormIsReady] = useState<boolean>(false);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const dispatch = useDispatch();
    const valid = useSelector((state: RootState) => state.validation.valid);
    const errors = useSelector((state: RootState) => state.validation.errors);

    const changeName = (e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setName(e.currentTarget.value)
        }
    }

    const handleSuccessResponse = (response: AxiosResponse, message: JSX.Element | string) => {
        const role = response.data.data as Role
        if (role) {
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {message}</>, "bg-teal-500");
            if (role.users_count > 0)
                navigate('/app/admin/roles');
            else navigate('/app/admin/roles');
        }
    }

    const callback = (b: boolean) => {
        setValidated(true);
        setInProgress(false);
        if (b) {
            const request = {name: name, permissions: selected, countries};
            if (id) {
                API.updateRole(parseInt(id ?? ''), request).then((response: AxiosResponse) => handleSuccessResponse(response, t('admin.roles.updated.title', {name}))).catch(e => ErrorHandler(e, `put${APILink.UPDATE_ROLE.replace('$role', id.toString())}`, toast, handleError));
            } else {
                API.createRole(request).then((response: AxiosResponse) => {
                    dispatch(removeDataTableRequest('/app/admin/roles'));
                    handleSuccessResponse(response, t('admin.roles.created.title', {name}));
                }).catch(e => ErrorHandler(e, `post${APILink.GET_ROLES}`, toast, handleError));
            }

        }
    }

    const sendCountriesRequest = useCallback(() => {
        API.getCountriesShortList().then((response: AxiosResponse) => {
            setListCountries(response.data as CountriesShortResponse);
            if (!id) {
                setFormIsReady(true);
            }
        }).catch(e => ErrorHandler(e, `get${APILink.GET_COUNTRIES_SHORT_LIST}`, toast, handleError));
    }, [toast, handleError]);

    const sendRegionsRequest = useCallback(() => {
        API.getRegionsShortList().then((response: AxiosResponse) => {
            setAllRegions(response.data as AllRegionsResponse);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_REGIONS_SHORT_LIST}`, toast, handleError));
    }, [toast, handleError]);


    const onCountryRemove = useCallback((index: number) => {
        if (countries) {
            const newCountries = [...countries];
            if (newCountries[index]) {
                newCountries.splice(index, 1)
            }
            setCountries(newCountries)
        }
    }, [countries, setCountries]);

    const changeRegions = useCallback((s: string | number) => {
        if (!allRegions)
            return;
        const newRegions = regions ? [...regions] : [];
        const filteredRegions = [...newRegions, typeof s === 'string' ? parseInt(s) : s].filter((value, index, array) => {
            return array.indexOf(value) === index;
        });
        const regionCountries = allRegions.data.filter(i => filteredRegions.includes(i.id)).map(r => r.countries);
        let newCountries = [];
        for (let i = 0; i < regionCountries.length; i++) {
            // @ts-expect-error can be undefined
            newCountries = newCountries.concat(regionCountries[i]);
        }
        setRegions(filteredRegions);
        setCountries(newCountries);
    }, [setRegions, regions, allRegions, setCountries]);

    const onRegionRemove = useCallback((index: number) => {
        if (regions && allRegions) {
            const newRegions = [...regions];
            if (newRegions[index]) {
                newRegions.splice(index, 1)
            }
            setRegions(newRegions);
            const regionCountries = allRegions.data.filter(i => newRegions.includes(i.id)).map(r => r.countries);
            let newCountries = [];
            for (let i = 0; i < regionCountries.length; i++) {
                // @ts-expect-error can be undefined
                newCountries = newCountries.concat(regionCountries[i]);
            }
            setCountries(newCountries);
        }
    }, [regions, allRegions, setRegions, setCountries]);

    const changeCountries = useCallback((s: string | number) => {
        const newCountries = countries ? [...countries] : [];
        setCountries([...newCountries, typeof s === 'string' ? parseInt(s) : s].filter((value, index, array) => {
            return array.indexOf(value) === index;
        }));
    }, [setCountries, countries]);

    const onSelectAllCountries = useCallback(() => {
        if (!listCountries)
            return;
        setCountries(listCountries.data.map(i => i.id));
    }, [listCountries, setCountries]);

    const onCountryClear = useCallback(() => {
        setCountries([]);
    }, []);

    const submit = () => {
        setInProgress(true);
        dispatch(validateData({
            payload: {
                inputs: [{key: 'name', label: t('admin.roles.table.name'), type: InputType.text, regex: '^.{4,}\\b$'}],
                introducedData: {name: name},
                callback: callback
            }
        }));
    }

    const onPermissionSelected = useCallback((e: ChangeEvent<HTMLInputElement> | undefined) => {
        if (e && e.target) {
            let values = [...selected];
            if (selected.includes(parseInt(e.target.value))) {
                values = values.filter(i => i !== parseInt(e.target.value));
            } else {
                values.push(parseInt(e.target.value));
            }
            setSelected(values)
        }
    }, [selected, setSelected]);

    const getSingleRole = useCallback(() => {
        API.getRole(parseInt(id ?? '')).then((response: AxiosResponse) => {
            const roleResponse = response.data.data as Role
            setName(roleResponse.name);
            setSelected(roleResponse.permissions);
            setCountries(roleResponse.countries);
            setFormIsReady(true);
            setTitle(t('admin.roles.update.title', {name: roleResponse.name}));
            dispatch(setSidebarTitle(t('admin.roles.update.title', {name: roleResponse.name})))
        }).catch(e => {
            ErrorHandler(e, `get${APILink.UPDATE_ROLE.replace('$role', id?.toString() ?? '')}`, toast, handleError, setIsForbidden);
            setFormIsReady(true);
        });
    }, [setName, setSelected, setFormIsReady, toast, dispatch, handleError, id]);

    useEffect(() => {
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                API.getPermissions().then((response: AxiosResponse) => {
                    const permissionsResponse = response.data as PermissionsResponse;
                    setPermissions(permissionsResponse.data);
                    if (!id)
                        setFormIsReady(true);
                }).catch(e => ErrorHandler(e, `get${APILink.GET_PERMISSIONS}`, toast, handleError));
                sendRegionsRequest();
                sendCountriesRequest();

                if (id) {
                    getSingleRole();
                } else {
                    setTitle(t('admin.roles.new.button'));
                    dispatch(setSidebarTitle(t('admin.roles.new.button')));
                    if (user && !user.user.permissions.includes('create role')) {
                        setIsForbidden(true)
                    }
                }
            }).catch()
        }
        dispatch(clear())
    }, [dispatch, id, getSingleRole, handleError, toast, user, sendRegionsRequest, sendCountriesRequest]);

    const permissionsUI = useMemo(() => {
        if (!permissions || !id)
            return <></>;

        return <div className="mt-4">
            <h4 className="block antialiased tracking-normal dark:text-gray-300 font-sans text-2xl font-semibold leading-snug text-inherit mt-8">{t('admin.roles.permissions')}</h4>
            <hr className="mt-2 mb-4 dark:text-gray-700 dark:bg-gray-700 dark:border-gray-700"/>
            <div className="grid md:grid-cols-2 lg:grid-cols-3">
                {permissions.map(permission => <div key={permission.name} className="mb-16">
                    <h4>{permission.name}</h4>
                    {permission.permissions.map(p => <div key={p.name}><Checkbox checked={selected.includes(p.id)}
                                                                                 disabled={parseInt(id) === 1}
                                                                                 onChange={onPermissionSelected}
                                                                                 color={'teal'} crossOrigin={''}
                                                                                 value={p.id} label={<span
                        className="dark:text-gray-500">{p.name}</span>}/></div>)}
                </div>)}
            </div>
        </div>
    }, [selected, permissions, onPermissionSelected, id]);


    return (
        <PageBlock>
            {!formIsReady ? <FormSkeleton/> : <>
                <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                    <CardBody className="overflow-auto px-0">
                        {!isForbidden && <>
                            <div className="max-w-md">
                                <Input success={valid && validated} error={validated && !valid} crossOrigin=''
                                       variant={'outlined'} label={t('admin.roles.table.name')} type={'text'}
                                       onChange={changeName}
                                       value={name}
                                       className="inline-block dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent"
                                       labelProps={{className: "dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800"}}/>
                                {hasError('name', errors) &&
                                    <label key={'error-name'}
                                           className="mt-1 text-red-500 text-xs block">{getError('name', errors)}</label>}
                            </div>
                            {parseInt(id ?? '') !== 1 ? <>
                                <div className="max-w-md mt-4">
                                    <DropdownInput id="regions_form" name={t('admin.payment_methods.regions')}
                                                   className="mb-4"
                                                   buttonClassName="min-h-[auto!important]"
                                                   searchable={true}
                                                   multiple={true}
                                                   selected={regions}
                                                   onChange={changeRegions}
                                                   onRemove={onRegionRemove}
                                                   list={allRegions?.data ?? []}/>
                                </div>

                                <div className="max-w-md mt-4">
                                    <DropdownInput name={t('admin.payment_methods.table.countries')}
                                                   buttonClassName="min-h-[auto!important]"
                                                   searchable={true}
                                                   multiple={true}
                                                   withAllButton={true}
                                                   selected={countries}
                                                   onChange={changeCountries}
                                                   onRemove={onCountryRemove}
                                                   onSelectAll={onSelectAllCountries}
                                                   onClear={onCountryClear}
                                                   list={listCountries?.data.map((item) => {
                                                       return {
                                                           id: item.id,
                                                           name: item.name,
                                                           icon: <CountryAvatar country={item} withName={false}
                                                                                className="h-3 max-w-[1rem]"/>
                                                       }
                                                   }) ?? []}/>

                                    {validated && hasError('countries', errors) &&
                                        <label key={'error-countries'}
                                               className="mt-1 text-red-500 text-xs block">{getError('countries', errors)}</label>}
                                </div>
                            </> : ''}
                            <div className="mt-4">
                                {permissionsUI}
                            </div>
                            <div className="mt-12">
                                <Button onClick={submit} className="text-white bg-teal-500"
                                        disabled={isInProgress}>{id ? t('admin.buttons.update') : t('admin.buttons.create')}</Button>
                            </div>
                        </>}
                        <Forbidden isForbidden={isForbidden}/>
                    </CardBody>
                </Card>
            </>}
        </PageBlock>
    );
}
export default memo(RoleForm);
